import { Alignment_Horizontal } from './shared/api_shared_types';
import { degreeToRad, v2 } from './helpers';
import {
  ButtonStyle,
  LineStyle,
  RenderShadow,
  RenderShapeStyle,
  TextObjectStyle,
  type GradientData,
  type TextStyle,
} from './types';
import { COLORS, GREEN_BUTTON, GREEN_BUTTON_HL } from './constants-colors';

export { COLORS } from './constants-colors';

export const startInDebugMode = true;

export const DEGREE_180_IN_RAD = degreeToRad(180);
export const DEGREE_90_IN_RAD = degreeToRad(90);

export const DEFAULT_CURSOR_ROTATION_SPEED = 40;
export const APPLY_KERNING = true;
export const BASE_LINE_HEIGHT_MOD = 1.25;
export const CANVAS_TEXT_CAP_MARGIN_RATIO = 0.08;
export const TEXTOBJ2_HANDLE_SIZE = 1;
export const TEXTOBJ2_MIN_WIDTH = 30;
export const TEXTOBJ2_MIN_HEIGHT = 10;
export const defaultFontSizePx = 20;
export const defaultRootUnitSizePx = 100;
export const TEXT_TAB_SIZE_MOD = 1.5;
export const autoTypeSpeed = 0.01;
export const autoTypeSpeedAudio = 0.04;
export const defaultCursorSize = 30;
export const INSTRUCTION_BAR_BOTTOM_PADDING = 120;

export const DIRECTION = {
  UP: v2(0, -1),
  RIGHT: v2(1, 0),
  DOWN: v2(0, 1),
  LEFT: v2(-1, 0),
} as const;

const SHADOW_DEFAULT: RenderShadow = {
  color: `rgba(0,0,0,0.15)`,
  blur: 24,
  offsetX: 0,
  offsetY: 0,
};

const SHADOW_DRAG: RenderShadow = {
  color: `rgba(0,0,0,0.2)`,
  blur: 32,
  offsetX: 4,
  offsetY: 4,
};

const SHADOW_BUTTON_HIGHLIGHT: RenderShadow = {
  color: `rgba(0,0,0,0.4)`,
  blur: 16,
  offsetX: 0,
  offsetY: 0,
};

const SHADOW_LAYER: RenderShadow = {
  color: `rgba(0,0,0,0.2)`,
  blur: 16,
  offsetX: 0,
  offsetY: 0,
};

const UNSHARP: RenderShadow = {
  color: `rgba(255,255,255,1)`,
  blur: 32,
  offsetX: 0,
  offsetY: 500,
};

const LOGO: RenderShadow = {
  color: `rgba(35,140,231,1)`,
  blur: 32,
  offsetX: 0,
  offsetY: 0,
};

const RED_GLOW: RenderShadow = {
  color: `rgb(60, 255, 0)`,
  blur: 32,
  offsetX: 0,
  offsetY: 0,
};

const TEXT_DEBUG: RenderShadow = {
  color: `rgba(0,0,0,1)`,
  blur: 12,
  offsetX: 0,
  offsetY: 0,
};

const SHADOW_TEXT: RenderShadow = {
  color: `rgba(0,0,0,1)`,
  blur: 4,
  offsetX: 0,
  offsetY: 0,
};

const SHADHOW_QUOTE: RenderShadow = {
  color: `rgba(0,0,0,1)`,
  blur: 32,
  offsetX: 0,
  offsetY: 0,
};
export const SHADOWS = {
  DEFAULT: SHADOW_DEFAULT,
  SHADOW_DRAG,
  SHADOW_BUTTON_HIGHLIGHT,
  SHADOW_LAYER,
  UNSHARP,
  LOGO,
  RED_GLOW,
  TEXT: SHADOW_TEXT,
  TEXT_DEBUG,
  QUOTE: SHADHOW_QUOTE,
};

const TEXT_BASE: TextObjectStyle = {
  color: [...COLORS.TRANSPARENT],
  colorHighlight: [...COLORS.TRANSPARENT],
  defaultTextColor: [...COLORS.BLACK_100],
  fontSizeEm: 1,
  doAutoResize: false,
  lineHeightMod: 1,
  alineaSpacingMod: 1,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 0,
  roundedSides: [false, false],
  cornerRadius: [0, 0, 0, 0],
  strokeWeight: 0,
  strokeColor: [...COLORS.TRANSPARENT],
};

const TEXT_DEFAULT: TextObjectStyle = {
  ...TEXT_BASE,
  color: [...COLORS.WHITE_100],
  colorHighlight: [...COLORS.WHITE_100],
  defaultTextColor: [...COLORS.BLACK_100],
  fontSizeEm: 1.05,
  doAutoResize: true,
  padding: [12, 12],
  roundedSides: [true, true],
};

const TEXT_LINK_POPUP: TextObjectStyle = {
  ...TEXT_DEFAULT,
  defaultTextColor: [...COLORS.BLUE_HIGHLIGHT],
  roundedSides: [false, false],
  fontSizeEm: 0.8,
  doAutoResize: false,
  padding: [12, 12],
};

const TEXT_DEFAULT_NEW: TextObjectStyle = {
  ...TEXT_BASE,

  defaultTextColor: [...COLORS.WHITE_100],
  fontSizeEm: 1.05,
  doAutoResize: true,
};

const TEXT_DEFAULT_TRANSPARANT: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: [...COLORS.TRANSPARENT],
  colorHighlight: [...COLORS.TRANSPARENT],
  padding: [0],
  roundedSides: [false, false],
};

const TEXT_CONTENT_DEFAULT: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1.05,
  lineHeightMod: 1.2,
  alineaSpacingMod: 2,
  padding: [0],
};

const TEXT_BLOCK: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.8,
  lineHeightMod: 1.2,
  alineaSpacingMod: 2,
  padding: [20],
  doAutoResize: false,
  // padding: [20, 20, 20, 20],
};

const TEXT_CONTENT_DEFAULT_SMALL: TextObjectStyle = {
  ...TEXT_CONTENT_DEFAULT,
  fontSizeEm: 0.8,
  // padding: [16, 16, 16, 16],
};

const TEXT_DEFAULT_PANEL: TextObjectStyle = {
  ...TEXT_CONTENT_DEFAULT,
  color: [...COLORS.BLACK_25],
  colorHighlight: [...COLORS.BLACK_25],
  padding: [40],
  lineHeightMod: 1.2,
};

const TEXT_NMTP: TextObjectStyle = {
  ...TEXT_DEFAULT_PANEL,
  padding: [40, 40, 80, 40],
  lineHeightMod: 1.1,
};

const TEXT_SOE_ITEM_CANVAS_TITLE: TextObjectStyle = {
  ...TEXT_CONTENT_DEFAULT,
  textAlign: Alignment_Horizontal.CENTER,
  lineHeightMod: 1,
  alineaSpacingMod: 1,
};

const TEXT_SOE_ITEM_CANVAS_CONTENT: TextObjectStyle = {
  ...TEXT_CONTENT_DEFAULT,
  textAlign: Alignment_Horizontal.CENTER,
  fontSizeEm: 0.8,
  lineHeightMod: 1,
  alineaSpacingMod: 2.5,
};

const TEXT_DEFAULT_NEW_SMALL: TextObjectStyle = {
  ...TEXT_DEFAULT_NEW,
  fontSizeEm: 0.7,
};

const TEXT_DEFAULT_WHITE: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: [...COLORS.WHITE_100],
};

const TEXT_IMAGE_LABEL: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.GUNMETAL,
  fontSizeEm: 1,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
  padding: [10, 0, 0, 0],
};

const TEXT_LIVE_SESSION_NODE_TITLE: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1.0,
  doAutoResize: false,
  fontId: 'font_bold',
  textAlign: Alignment_Horizontal.CENTER,
  padding: 0,
};

const TEXT_LIVE_SESSION_NODE_TITLE_NEW: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1.1,
  doAutoResize: false,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 0,
};

const TEXT_LIVE_SESSION_NODE_SUB_TITLE: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_50,
  fontSizeEm: 0.7,
  doAutoResize: false,
  fontId: 'font_bold',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 0,
};

const TEXT_LIVE_SESSION_NODE_INFO: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.8,
  doAutoResize: false,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 0,
};

const TEXT_LIVE_SESSION_NODE_BUBBEL: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.8,
  doAutoResize: false,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
  padding: 0,
};

const TEXT_LIVE_SESSION_NODE_BUBBEL_DARK: TextObjectStyle = {
  ...TEXT_LIVE_SESSION_NODE_BUBBEL,
  defaultTextColor: COLORS.BLACK_100,
};

const TEXT_RESILIENCE_CANVAS: TextObjectStyle = {
  ...TEXT_BASE,

  defaultTextColor: [...COLORS.WHITE_100],

  fontId: 'font_bold',
  textAlign: Alignment_Horizontal.LEFT,
  padding: [37, 30],
};

const TEXT_RESILIENCE_CANVAS_TITLE: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  color: COLORS.GUNMETAL,
  defaultTextColor: [...COLORS.WHITE_100],
  fontSizeEm: 0.8,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
  doAutoResize: false,
  padding: 20,
  // cornerRadius: [10, 10, 10, 10],
};

const TEXT_RESILIENCE_CANVAS_CONTENT: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  // color: COLORS.WHITE_25,
  defaultTextColor: [...COLORS.WHITE_100],
  fontSizeEm: 0.8,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  doAutoResize: false,
  alineaSpacingMod: 3,
};

const TEXT_CMS_CONSOLE_TEXT: TextObjectStyle = {
  ...TEXT_DEFAULT_NEW,

  doAutoResize: false,
  fontSizeEm: 0.5,
  padding: [0],
};

const TEXT_CMS_CONSOLE_BUTTON: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  defaultTextColor: COLORS.WHITE_100,
  cornerRadius: [0, 0, 0, 0],
  doAutoResize: false,
  roundedSides: [false, false],
  fontSizeEm: 0.5,
  padding: [5, 0, 5, 0],
};

const TEXT_CHAT_MSG_SELF: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.WHITE_10,
  colorHighlight: COLORS.WHITE_50,
  defaultTextColor: COLORS.WHITE_100,
  cornerRadius: [0, 0, 0, 0],
  roundedSides: [false, false],
  fontSizeEm: 0.8,
  padding: [10, 10, 10, 10],
};

const TEXT_CHAT_MSG_OTHER: TextObjectStyle = {
  ...TEXT_CHAT_MSG_SELF,
  color: COLORS.WHITE_50,
};

const TEXT_CHAT_MSG_TIME: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.6,
};

const TEXT_LIVE_SESSION_DATE: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.TRANSPARENT,
  colorHighlight: COLORS.TRANSPARENT,
  defaultTextColor: COLORS.LIGHTGREY1_100,
  fontSizeEm: 0.8,
  roundedSides: [false, false],
  padding: [0, 0],
  doAutoResize: false,
  textAlign: Alignment_Horizontal.LEFT,
};

const TEXT_LIVE_SESSION_TIME: TextObjectStyle = {
  ...TEXT_LIVE_SESSION_DATE,
  defaultTextColor: COLORS.LIGHTGREY1_75,
  textAlign: Alignment_Horizontal.LEFT,
};

const TEXT_LIVE_SESSION_DATE_NEW: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.TRANSPARENT,
  colorHighlight: COLORS.TRANSPARENT,
  defaultTextColor: COLORS.BLACK_100,
  fontSizeEm: 0.74,
  roundedSides: [false, false],
  padding: [0, 0],
  doAutoResize: false,
  textAlign: Alignment_Horizontal.CENTER,
};

const TEXT_LIVE_SESSION_TIME_NEW: TextObjectStyle = {
  ...TEXT_LIVE_SESSION_DATE_NEW,
  fontSizeEm: 0.64,
  fontId: 'font_bold',
  defaultTextColor: COLORS.BLACK_100,
  textAlign: Alignment_Horizontal.CENTER,
};

const TEXT_LIVE_SESSION_JOIN_BUTTON: TextObjectStyle = {
  ...TEXT_LIVE_SESSION_DATE,
  color: COLORS.BLUE_CRAYOLA,
  colorHighlight: COLORS.BLUE_HIGHLIGHT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1,
  cornerRadius: [10, 10, 10, 10],
  padding: [15, 40],
};

const TEXT_LIVE_SESSION_JOIN_BUTTON_NEW: TextObjectStyle = {
  ...TEXT_LIVE_SESSION_DATE,
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_25,
  defaultTextColor: COLORS.BLACK_100,
  roundedSides: [true, true],
  fontSizeEm: 1,
  cornerRadius: [0, 0, 0, 0],
  padding: [15, 30, 15, 0],
};

const TEXT_LIVE_SESSION_JOIN_BUTTON_DEBUG: TextObjectStyle = {
  ...TEXT_LIVE_SESSION_DATE,
  color: COLORS.NEW_STYLE.GREEN_LIGHT,
  colorHighlight: COLORS.GREEN_HIGHLIGHT,
  defaultTextColor: COLORS.BLACK_100,
  fontSizeEm: 0.8,
  cornerRadius: [10, 10, 10, 10],
  padding: [8, 8],
};

const TEXT_LIVE_SESSION_INFO_BUTTON: TextObjectStyle = {
  ...TEXT_LIVE_SESSION_DATE,
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1,
  cornerRadius: [10, 10, 10, 10],
  padding: [15, 40],
};

const TEXT_CHAT_MSG_DATE: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.6,
  fontId: 'font_bold',
  textAlign: Alignment_Horizontal.CENTER,
};

const TEXT_THE_WAY_RULES: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1.5,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
  padding: [30, 30, 30, 30],
};

const TEXT_LABEL: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.BLACK_100,
  fontSizeEm: 1,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
};

const TEXT_ZOOM_INFO: TextObjectStyle = {
  ...TEXT_DEFAULT,
  defaultTextColor: COLORS.BLACK_100,
  fontSizeEm: 0.6,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 6,
  doAutoResize: false,
};

const TEXT_LABEL_DROPAREA: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_75,
  fontSizeEm: 0.8,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
};

const TEXT_LABEL_DROPAREA_CHARCOUNT: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.6,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: [2, 4],
};

const TEXT_LABEL_DROPAREA_CHARCOUNT_ERR: TextObjectStyle = {
  ...TEXT_LABEL_DROPAREA_CHARCOUNT,
  color: COLORS.RED_SALSA,
  colorHighlight: COLORS.RED_SALSA,
  // defaultTextColor: COLORS.RED_SALSA,
};

const TEXT_USER_TEXT: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.WHITE_100,
  colorHighlight: COLORS.WHITE_100,
  defaultTextColor: COLORS.BLACK_100,
  fontSizeEm: 1,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  roundedSides: [false, false],
  padding: [12, 12, 12, 12],
  cornerRadius: [4, 4, 4, 4],
  doAutoResize: false,
};

const TEXT_USER_TEXT_SMALL: TextObjectStyle = {
  ...TEXT_USER_TEXT,
  fontSizeEm: 0.7,
};

const TEXT_REFRAME_NEGATIVE: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: [...COLORS.RED_SALSA, 100],
  colorHighlight: [...COLORS.RED_SALSA, 150],
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.9,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
  roundedSides: [false, false],
  padding: [12, 12, 15, 12],
  cornerRadius: [0, 0, 0, 0],
};

const TEXT_REFRAME_POSITIVE: TextObjectStyle = {
  ...TEXT_REFRAME_NEGATIVE,
  color: [...COLORS.BLUE_CRAYOLA, 100],
  colorHighlight: [...COLORS.BLUE_CRAYOLA, 150],
  defaultTextColor: COLORS.WHITE_100,
  padding: [15, 12, 12, 12],
};

const TEXT_REFRAME_SUB_NEGATIVE: TextObjectStyle = {
  ...TEXT_REFRAME_NEGATIVE,
  fontSizeEm: 0.7,
  padding: [6, 6, 6, 6],

  fontId: 'font_regular',
};

const TEXT_REFRAME_SUB_POSITIVE: TextObjectStyle = {
  ...TEXT_REFRAME_POSITIVE,
  fontSizeEm: 0.7,
  padding: [6, 6, 6, 6],

  fontId: 'font_regular',
};

const TEXT_REFRAME_TOOLTIP: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_25,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.8,
  padding: [6, 6, 6, 6],
  roundedSides: [false, false],

  fontId: 'font_regular',
};

const TEXT_USER_TEXT_MSG: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.BLACK_25,
  colorHighlight: COLORS.BLACK_25,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.7,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  roundedSides: [true, true],
  padding: [5, 5],
  cornerRadius: [0, 0, 0, 0],
};
const TEXT_USER_TEXT_CURSOR: TextObjectStyle = {
  ...TEXT_DEFAULT,
  roundedSides: [false, true],
  padding: [12, 36, 12, 12],
  cornerRadius: [12, 12, 12, 12],
};

const TEXT_USER_TEXT_CURSOR_NO_FOCUS: TextObjectStyle = {
  ...TEXT_USER_TEXT_CURSOR,
  color: COLORS.WHITE_50,
};

const TEXT_CURSOR_PLACEHOLDER: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.BLACK_50,
  fontSizeEm: 0.7,
  padding: [0, 24, 0, 0],
};

const TEXT_DARK_ON_LIGHT: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.GUNMETAL,
};

const TEXT_FLOATING: TextObjectStyle = {
  ...TEXT_DEFAULT,
  doAutoResize: false,
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  defaultTextColor: COLORS.WHITE_100,
  cornerRadius: [0, 0, 0, 0],
  padding: [20, 20, 20, 20],
  roundedSides: [false, false],
};

const TEXT_FLOATING_ROUNDED: TextObjectStyle = {
  ...TEXT_FLOATING,
  cornerRadius: [20, 20, 20, 20],
};

const TEXT_FLOATING_LIGHT: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.WHITE_10,
  colorHighlight: COLORS.WHITE_10,
  defaultTextColor: COLORS.WHITE_100,
  padding: [20, 20, 20, 20],
  roundedSides: [false, false],
};

const TEXT_FLOATING_LIGHT_ROUNDED: TextObjectStyle = {
  ...TEXT_FLOATING_LIGHT,
  cornerRadius: [20, 20, 20, 20],
};

const TEXT_FLOATING_HL: TextObjectStyle = {
  ...TEXT_FLOATING,
  color: COLORS.TRANSPARENT,
  colorHighlight: COLORS.TRANSPARENT,
  defaultTextColor: COLORS.WHITE_100,
  padding: [20, 20, 20, 20],
  roundedSides: [false, false],
};

const TEXT_FLOATING_HL_ROUNDED: TextObjectStyle = {
  ...TEXT_FLOATING_HL,
  cornerRadius: [20, 20, 20, 20],
};

const TEXT_FLOATING_SMALL: TextObjectStyle = {
  ...TEXT_FLOATING,
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.7,
  padding: [16, 16, 16, 16],
  cornerRadius: [3, 3, 3, 3],
};

const TEXT_FLOATING_GREEN: TextObjectStyle = {
  ...TEXT_FLOATING,
  color: COLORS.GREEN_25,
  colorHighlight: COLORS.GREEN_50,
  fontSizeEm: 0.8,
};

const TEXT_FLOATING_RED: TextObjectStyle = {
  ...TEXT_FLOATING,
  color: COLORS.RED_25,
  colorHighlight: COLORS.RED_50,
  fontSizeEm: 0.8,
};

const TEXT_FLOATING_QUESTION: TextObjectStyle = {
  ...TEXT_FLOATING,
  fontSizeEm: 0.9,
  padding: [20, 20, 20, 50],
  alineaSpacingMod: 2,
  roundedSides: [true, true],
};

const TEXT_FLOATING_QUESTION_NEW: TextObjectStyle = {
  ...TEXT_FLOATING,
  fontSizeEm: 0.9,
  padding: [20, 20, 20, 50],
  alineaSpacingMod: 2,
  color: COLORS.TRANSPARENT,
  roundedSides: [true, true],
};

const TEXT_LIGHT_ON_DARK: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.GAINSBORO,
  fontSizeEm: 1.1,
  lineHeightMod: 1,
  alineaSpacingMod: 1,
  padding: [20, 20, 20, 20],
};

const TEXT_CANVAS_DEFAULT: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1.1,
  padding: [0],
};

const TEXT_PAGED_LIGHT_ON_DARK: TextObjectStyle = {
  ...TEXT_LIGHT_ON_DARK,
  textAlign: Alignment_Horizontal.CENTER,
};

const TEXT_CONTENT_TITLE: TextObjectStyle = {
  ...TEXT_CONTENT_DEFAULT,
  fontSizeEm: 1.8,
  alineaSpacingMod: 0.5,
  textAlign: Alignment_Horizontal.CENTER,
  fontId: 'font_bold',
};

const TEXT_CONTENT_SUBTITLE: TextObjectStyle = {
  ...TEXT_CONTENT_DEFAULT,
  fontSizeEm: 1.5,
  alineaSpacingMod: 0.5,
  lineHeightMod: 0.9,
  textAlign: Alignment_Horizontal.CENTER,
};

const TEXT_CONTENT_SUBTITLE_SOFT: TextObjectStyle = {
  ...TEXT_CONTENT_SUBTITLE,
  defaultTextColor: COLORS.WHITE_50,
};

const TEXT_CONTENT_TITLE_BIG: TextObjectStyle = {
  ...TEXT_CONTENT_DEFAULT,
  fontSizeEm: 4,
  textAlign: Alignment_Horizontal.CENTER,
  fontId: 'font_extrabold',
  lineHeightMod: 0.8,
  alineaSpacingMod: 1,
};

const TEXT_CONTENT_TITLE_MISSION: TextObjectStyle = {
  ...TEXT_CONTENT_DEFAULT,
  fontSizeEm: 12,
  textAlign: Alignment_Horizontal.CENTER,
  fontId: 'font_extrabold',
  lineHeightMod: 0.8,
  alineaSpacingMod: 1,
};

const TEXT_LIGHT_ON_DARK_SMALL: TextObjectStyle = {
  ...TEXT_LIGHT_ON_DARK,
  fontSizeEm: 0.9,
  textAlign: Alignment_Horizontal.LEFT,
};

const TEXT_HOME: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.GAINSBORO,
  fontSizeEm: 2,
};

const TEXT_CURSOR: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.GUNMETAL,
  colorHighlight: COLORS.WHITE_100,
  defaultTextColor: COLORS.GAINSBORO,
  fontSizeEm: 1,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: [12, 60, 12, 12],
};

const TEXT_BUTTON: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  defaultTextColor: COLORS.WHITE_100,
  cornerRadius: [0, 0, 0, 0],
  roundedSides: [false, false],
  fontSizeEm: 0.8,
  padding: [12, 0, 12, 0],
};

const TEXT_BUTTON_FIXED: TextObjectStyle = {
  ...TEXT_BUTTON,
  fontSizeEm: 0.6,
  doAutoResize: false,
};

const TEXT_BUTTON_FIXED_RED_DARK: TextObjectStyle = {
  ...TEXT_BUTTON,
  color: COLORS.RED_DARK,
  colorHighlight: COLORS.RED_DARK_HL,
  fontSizeEm: 0.6,
  doAutoResize: false,
};

const TEXT_BUTTON_FIXED_GREEN: TextObjectStyle = {
  ...TEXT_BUTTON,
  color: COLORS.GREEN_100,
  colorHighlight: COLORS.GREEN_HIGHLIGHT,
  fontSizeEm: 0.6,
  doAutoResize: false,
};

const TEXT_BUTTON_FIXED_BLUE: TextObjectStyle = {
  ...TEXT_BUTTON,
  color: COLORS.BLUE_CRAYOLA,
  colorHighlight: COLORS.BLUE_HIGHLIGHT,
  fontSizeEm: 0.6,
  doAutoResize: false,
};

const TEXT_BUTTON_BIG: TextObjectStyle = {
  ...TEXT_BUTTON,
  fontSizeEm: 1.2,
  padding: [20, 0, 20, 0],
};

const TEXT_BUTTON_AI: TextObjectStyle = {
  ...TEXT_BUTTON,
  fontSizeEm: 1.6,
  padding: [40, 0, 40, 40],
  cornerRadius: [20, 20, 0, 0],
};

const TEXT_BUTTON_BIG_FIXED: TextObjectStyle = {
  ...TEXT_BUTTON_BIG,
  fontSizeEm: 1,
  doAutoResize: false,
};

const TEXT_BUTTON_BIG_FIXED_GREEN: TextObjectStyle = {
  ...TEXT_BUTTON_BIG,
  color: COLORS.GREEN_25,
  colorHighlight: COLORS.GREEN_50,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1,
  doAutoResize: false,
};

const TEXT_BUTTON_GREEN: TextObjectStyle = {
  ...TEXT_BUTTON,
  color: GREEN_BUTTON,
  colorHighlight: GREEN_BUTTON_HL,
  defaultTextColor: COLORS.BLACK_100,
  fontSizeEm: 0.6,
  doAutoResize: false,
};

const TEXT_BUTTON_LEAVE_CALL: TextObjectStyle = {
  ...TEXT_BUTTON,
  color: COLORS.RED_SALSA,
  colorHighlight: COLORS.RED_HIGHLIGHT,
};

const TEXT_READY_CHECK_LABEL: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.65,
  padding: 0,
};

const TEXT_ICON_BUTTON_LABEL: TextObjectStyle = {
  ...TEXT_CONTENT_DEFAULT,
  color: COLORS.GUNMETAL,
  colorHighlight: COLORS.GUNMETAL,
  defaultTextColor: COLORS.GAINSBORO,
  fontSizeEm: 0.7,
  padding: [8, 5, 8, 5],
  roundedSides: [true, true],
  doAutoResize: false,
};

const TEXT_INSTRUCTION: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1.2,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 0,
};

const TEXT_PREPARATION: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1.2,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 0,
};

const TEXT_WHY_ITEM: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  padding: [20],
  roundedSides: [false, false],
};

const TEXT_WHY_ITEM_TITLE: TextObjectStyle = {
  ...TEXT_WHY_ITEM,
  fontSizeEm: 1.5,
};

const TEXT_WHY_ITEM_NAME: TextObjectStyle = {
  ...TEXT_WHY_ITEM,
  fontSizeEm: 0.7,
};

const TEXT_QUOTE: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 5,
  lineHeightMod: 0.8,
  fontId: 'font_bold',
  textAlign: Alignment_Horizontal.CENTER,
  padding: 0,
};

const TEXT_QUOTE_SMALL: TextObjectStyle = {
  ...TEXT_QUOTE,
  fontSizeEm: 3,
};

const TEXT_QUOTE_SMALLER: TextObjectStyle = {
  ...TEXT_QUOTE,
  fontSizeEm: 2,
};

const TEXT_QUOTE_AUTHOR: TextObjectStyle = {
  ...TEXT_QUOTE,

  fontSizeEm: 1.2,
  fontId: 'font_regular',
};

const TEXT_CURSOR_TOOLTIP: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  // color: COLORS.BLACK_25,
  // colorHighlight: COLORS.BLACK_25,
  defaultTextColor: COLORS.WHITE_100,
  // roundedSides: [false, false],
  fontSizeEm: 0.8,
  // cornerRadius: [0, 0, 0, 0],
  // padding: 5,
};

const TEXT_QUESTION_BIG: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 2,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
  padding: 0,
};

const TEXT_QUESTION_SMALL: TextObjectStyle = {
  ...TEXT_QUESTION_BIG,
  textAlign: Alignment_Horizontal.CENTER,
  fontSizeEm: 1.5,
};

const TEXT_TIPLABEL: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 1,
  fontId: 'font_bold',
  textAlign: Alignment_Horizontal.CENTER,
  padding: [10, 10],
};

const TEXT_TIPPOPUP: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.WHITE_100,
  colorHighlight: COLORS.WHITE_100,
  defaultTextColor: COLORS.BLACK_100,
  fontSizeEm: 0.9,
  roundedSides: [false, false],
  cornerRadius: [12, 12, 12, 12],
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 20,
};

const TEXT_RESTVALUES: TextObjectStyle = {
  ...TEXT_DEFAULT_TRANSPARANT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.75,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 0,
  doAutoResize: false,
};

const TEXT_ROOM_NODE: TextObjectStyle = {
  ...TEXT_LABEL,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.9,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
  padding: 20,
  doAutoResize: false,
};

const TEXT_ROOM_NODE_BIG: TextObjectStyle = {
  ...TEXT_ROOM_NODE,
  fontSizeEm: 2,
};

const TEXT_ROOM_NODE_CIRCLE: TextObjectStyle = {
  ...TEXT_ROOM_NODE,
  fontSizeEm: 1,
};

const TEXT_ROOM_NODE_LOCKED: TextObjectStyle = {
  ...TEXT_ROOM_NODE,
  defaultTextColor: COLORS.WHITE_50,
};

const TEXT_ROOM_NODE_BIG_LOCKED: TextObjectStyle = {
  ...TEXT_ROOM_NODE_BIG,
  defaultTextColor: COLORS.WHITE_50,
};

const TEXT_ROOM_NODE_INFO: TextObjectStyle = {
  ...TEXT_ROOM_NODE,
  fontId: 'font_regular',
  padding: [0, 20, 20, 20],
  fontSizeEm: 0.8,
};
const TEXT_SOCIAL_ROOM_NODE_TITLE: TextObjectStyle = {
  ...TEXT_DEFAULT_NEW,
  fontSizeEm: 1.6,
  fontId: 'font_bold',
  textAlign: Alignment_Horizontal.CENTER,
  doAutoResize: false,
};

const TEXT_SOCIAL_ROOM_NODE_COUNT: TextObjectStyle = {
  ...TEXT_DEFAULT_NEW,
  fontSizeEm: 0.7,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
  doAutoResize: false,
};

const TEXT_VIDEOPLAYER_MSG: TextObjectStyle = {
  ...TEXT_DEFAULT,
  color: COLORS.BLACK_50,
  colorHighlight: COLORS.BLACK_50,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.75,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.LEFT,
  padding: 6,
};

const TEXT_SOE_STRENGTH: TextObjectStyle = {
  ...TEXT_BASE,
  color: COLORS.RED_SALSA,
  colorHighlight: COLORS.RED_HIGHLIGHT,
  defaultTextColor: COLORS.WHITE_100,
  fontSizeEm: 0.8,
  fontId: 'font_regular',
  textAlign: Alignment_Horizontal.CENTER,
  padding: [25, 15, 15, 15],
  doAutoResize: false,
  lineHeightMod: 1,
  alineaSpacingMod: 0,
  roundedSides: [true, true],
  cornerRadius: [0, 0, 0, 0],
};

const TEXT_SOE_PASSION: TextObjectStyle = {
  ...TEXT_SOE_STRENGTH,
  color: COLORS.BLUE_CRAYOLA,
  colorHighlight: COLORS.BLUE_HIGHLIGHT,
};

const TEXT_SOE_VALUE: TextObjectStyle = {
  ...TEXT_SOE_STRENGTH,
  color: COLORS.GREEN_100,
  colorHighlight: COLORS.GREEN_HIGHLIGHT,
};

export const TEXT_STYLE = {
  BASE: TEXT_BASE,
  DEFAULT: TEXT_DEFAULT,
  DEFAULT_TRANSPARANT: TEXT_DEFAULT_WHITE,
  DEFAULT_NEW: TEXT_DEFAULT_NEW,
  SOE_ITEM_CANVAS_TITLE: TEXT_SOE_ITEM_CANVAS_TITLE,
  SOE_ITEM_CANVAS_CONTENT: TEXT_SOE_ITEM_CANVAS_CONTENT,
  DEFAULT_PANEL: TEXT_DEFAULT_PANEL,
  NMTP: TEXT_NMTP,
  DEFAULT_NEW_SMALL: TEXT_DEFAULT_NEW_SMALL,
  PAGED_TEXT_LIGHT_ON_DARK: TEXT_PAGED_LIGHT_ON_DARK,
  USER_TEXT: TEXT_USER_TEXT,
  USER_TEXT_SMALL: TEXT_USER_TEXT_SMALL,
  USER_TEXT_MSG: TEXT_USER_TEXT_MSG,
  USER_TEXT_CURSOR: TEXT_USER_TEXT_CURSOR,
  USER_TEXT_CURSOR_NO_FOCUS: TEXT_USER_TEXT_CURSOR_NO_FOCUS,
  CURSOR_PLACEHOLDER: TEXT_CURSOR_PLACEHOLDER,
  ZOOM_INFO: TEXT_ZOOM_INFO,
  LABEL_DROPAREA: TEXT_LABEL_DROPAREA,
  LABEL_DROPAREA_CHARCOUNT: TEXT_LABEL_DROPAREA_CHARCOUNT,
  LABEL_DROPAREA_CHARCOUNT_ERR: TEXT_LABEL_DROPAREA_CHARCOUNT_ERR,
  ROOM_NODE: TEXT_ROOM_NODE,
  ROOM_NODE_BIG: TEXT_ROOM_NODE_BIG,
  ROOM_NODE_CIRCLE: TEXT_ROOM_NODE_CIRCLE,
  ROOM_NODE_LOCKED: TEXT_ROOM_NODE_LOCKED,
  ROOM_NODE_BIG_LOCKED: TEXT_ROOM_NODE_BIG_LOCKED,
  ROOM_NODE_INFO: TEXT_ROOM_NODE_INFO,
  SOCIAL_ROOM_NODE_TITLE: TEXT_SOCIAL_ROOM_NODE_TITLE,
  SOCIAL_ROOM_NODE_COUNT: TEXT_SOCIAL_ROOM_NODE_COUNT,
  QUOTE: TEXT_QUOTE,
  QUOTE_SMALL: TEXT_QUOTE_SMALL,
  QUOTE_SMALLER: TEXT_QUOTE_SMALLER,
  QUOTE_AUTHOR: TEXT_QUOTE_AUTHOR,
  CURSOR_TOOLTIP: TEXT_CURSOR_TOOLTIP,
  INSTRUCTION: TEXT_INSTRUCTION,
  PREPARATION: TEXT_PREPARATION,
  QUESTION_BIG: TEXT_QUESTION_BIG,
  QUESTION_SMALL: TEXT_QUESTION_SMALL,
  TIPLABEL: TEXT_TIPLABEL,
  TIPPOPUP: TEXT_TIPPOPUP,
  RESTVALUES: TEXT_RESTVALUES,

  VIDEOPLAYER_MSG: TEXT_VIDEOPLAYER_MSG,
  BUTTON: TEXT_BUTTON,
  BUTTON_FIXED: TEXT_BUTTON_FIXED,
  BUTTON_FIXED_RED_DARK: TEXT_BUTTON_FIXED_RED_DARK,
  BUTTON_FIXED_GREEN: TEXT_BUTTON_FIXED_GREEN,
  BUTTON_FIXED_BLUE: TEXT_BUTTON_FIXED_BLUE,
  BUTTON_BIG: TEXT_BUTTON_BIG,
  BUTTON_AI: TEXT_BUTTON_AI,
  BUTTON_BIG_FIXED: TEXT_BUTTON_BIG_FIXED,
  BUTTON_BIG_FIXED_GREEN: TEXT_BUTTON_BIG_FIXED_GREEN,
  BUTTON_LEAVE_CALL: TEXT_BUTTON_LEAVE_CALL,
  BUTTON_GREEN: TEXT_BUTTON_GREEN,
  READY_CHECK_LABEL: TEXT_READY_CHECK_LABEL,
  DARK_ON_LIGHT: TEXT_DARK_ON_LIGHT,
  FLOATING: TEXT_FLOATING,
  FLOATING_LIGHT: TEXT_FLOATING_LIGHT,
  FLOATING_HL: TEXT_FLOATING_HL,

  FLOATING_ROUNDED: TEXT_FLOATING_ROUNDED,
  FLOATING_LIGHT_ROUNDED: TEXT_FLOATING_LIGHT_ROUNDED,
  FLOATING_HL_ROUNDED: TEXT_FLOATING_HL_ROUNDED,

  FLOATING_SMALL: TEXT_FLOATING_SMALL,
  FLOATING_GREEN: TEXT_FLOATING_GREEN,
  FLOATING_RED: TEXT_FLOATING_RED,
  FLOATING_QUESTION: TEXT_FLOATING_QUESTION,
  FLOATING_QUESTION_NEW: TEXT_FLOATING_QUESTION_NEW,
  LIGHT_ON_DARK: TEXT_LIGHT_ON_DARK,
  TEXT_BLOCK: TEXT_BLOCK,
  CONTENT_DEFAULT: TEXT_CONTENT_DEFAULT,
  CONTENT_DEFAULT_SMALL: TEXT_CONTENT_DEFAULT_SMALL,
  CANVAS_DEFAULT: TEXT_CANVAS_DEFAULT,
  HOME: TEXT_HOME,
  CONTENT_TITLE: TEXT_CONTENT_TITLE,
  CONTENT_SUBTITLE: TEXT_CONTENT_SUBTITLE,
  CONTENT_SUBTITLE_SOFT: TEXT_CONTENT_SUBTITLE_SOFT,
  CONTENT_TITLE_BIG: TEXT_CONTENT_TITLE_BIG,
  CONTENT_TITLE_MISSION: TEXT_CONTENT_TITLE_MISSION,
  LIGHT_ON_DARK_SMALL: TEXT_LIGHT_ON_DARK_SMALL,
  REFRAME_NEGATIVE: TEXT_REFRAME_NEGATIVE,
  REFRAME_POSITIVE: TEXT_REFRAME_POSITIVE,
  REFRAME_SUB_NEGATIVE: TEXT_REFRAME_SUB_NEGATIVE,
  REFRAME_SUB_POSITIVE: TEXT_REFRAME_SUB_POSITIVE,
  REFRAME_TOOLTIP: TEXT_REFRAME_TOOLTIP,
  IMAGE_LABEL: TEXT_IMAGE_LABEL,
  THE_WAY_RULES: TEXT_THE_WAY_RULES,
  RESILIENCE_CANVAS: TEXT_RESILIENCE_CANVAS,
  RESILIENCE_CANVAS_TITLE: TEXT_RESILIENCE_CANVAS_TITLE,
  RESILIENCE_CANVAS_CONTENT: TEXT_RESILIENCE_CANVAS_CONTENT,
  CMS_CONSOLE_TEXT: TEXT_CMS_CONSOLE_TEXT,
  CMS_CONSOLE_BUTTON: TEXT_CMS_CONSOLE_BUTTON,
  CHAT_MSG_SELF: TEXT_CHAT_MSG_SELF,
  CHAT_MSG_OTHER: TEXT_CHAT_MSG_OTHER,
  CHAT_MSG_TIME: TEXT_CHAT_MSG_TIME,
  CHAT_MSG_DATE: TEXT_CHAT_MSG_DATE,
  SOE_STRENGTH: TEXT_SOE_STRENGTH,
  SOE_PASSION: TEXT_SOE_PASSION,
  SOE_VALUE: TEXT_SOE_VALUE,
  LINK_POPUP: TEXT_LINK_POPUP,

  LIVE_SESSION_NODE: {
    TITLE: TEXT_LIVE_SESSION_NODE_TITLE,
    DATE: TEXT_LIVE_SESSION_DATE,
    TIME: TEXT_LIVE_SESSION_TIME,
    JOIN_BUTTON: TEXT_LIVE_SESSION_JOIN_BUTTON,
    JOIN_BUTTON_DEBUG: TEXT_LIVE_SESSION_JOIN_BUTTON_DEBUG,
  },

  LIVE_SESSION_NODE_NEW: {
    TITLE: TEXT_LIVE_SESSION_NODE_TITLE_NEW,
    SUB_TITLE: TEXT_LIVE_SESSION_NODE_SUB_TITLE,
    INFO: TEXT_LIVE_SESSION_NODE_INFO,
    BUBBEL: TEXT_LIVE_SESSION_NODE_BUBBEL,
    BUBBEL_DARK: TEXT_LIVE_SESSION_NODE_BUBBEL_DARK,
    DATE: TEXT_LIVE_SESSION_DATE_NEW,
    TIME: TEXT_LIVE_SESSION_TIME_NEW,
    JOIN_BUTTON: TEXT_LIVE_SESSION_JOIN_BUTTON_NEW,
  },

  ICON_BUTTON_LABEL: TEXT_ICON_BUTTON_LABEL,
} as const;

const LINE_NONE: LineStyle = {
  color: [0, 0, 0],
  dash: [],
  width: 0,
  bezier: false,
};

const LINE_DEFAULT: LineStyle = {
  color: COLORS.WHITE_100,
  dash: [],
  width: 4,
  bezier: false,
};

const LINE_BLUE: LineStyle = {
  color: COLORS.NEW_STYLE.BLUE_LIGHT,
  dash: [],
  width: 3,
  bezier: true,
};

const LINE_INSTRUCTION: LineStyle = {
  color: COLORS.WHITE_16,
  dash: [],
  width: 1.5,
  bezier: false,
};

const LINE_STEPS: LineStyle = {
  ...LINE_DEFAULT,
  dash: [1, 4],
  width: 2,
};

const LINE_STEPS_COMPLETED: LineStyle = {
  ...LINE_DEFAULT,
  dash: [],
  width: 2,
};

const LINE_CURSOR_INPUT: LineStyle = {
  ...LINE_DEFAULT,
  dash: [],
  width: 4,
};

const LINE_MODULE_PARTS: LineStyle = {
  ...LINE_DEFAULT,
  dash: [],
  width: 2,
  bezier: true,
};

const LINE_MODULE_PARTS_OPTIONAL: LineStyle = {
  ...LINE_DEFAULT,
  dash: [3, 7],
  width: 1.2,
  bezier: true,
};

const LINE_MODULE_PARTS_LOCKED: LineStyle = {
  ...LINE_MODULE_PARTS,
  color: COLORS.WHITE_50,
  bezier: true,
};

const LINE_MODULE_PARTS_OPTIONAL_LOCKED: LineStyle = {
  ...LINE_MODULE_PARTS_OPTIONAL,
  color: COLORS.WHITE_50,
  bezier: true,
};

const LINE_REFRAME_NEGATIVE: LineStyle = {
  color: COLORS.RED_SALSA,
  dash: [],
  width: 2,
  bezier: true,
};

const LINE_REFRAME_POSITIVE: LineStyle = {
  ...LINE_REFRAME_NEGATIVE,
  color: COLORS.BLUE_CRAYOLA,
};

const LINE_REFRAME_OBJ_NEGATIVE: LineStyle = {
  color: COLORS.RED_SALSA,
  dash: [],
  width: 2,
  bezier: false,
};

const LINE_REFRAME_OBJ_POSITIVE: LineStyle = {
  ...LINE_REFRAME_OBJ_NEGATIVE,
  color: COLORS.BLUE_CRAYOLA,
};

const LINE_THIN: LineStyle = {
  color: COLORS.WHITE_100,
  dash: [],
  width: 1,
  bezier: true,
};

const LINE_DOTTED: LineStyle = {
  color: COLORS.WHITE_100,
  dash: [5, 6],
  width: 1,
  bezier: true,
};

const LINE_THICK: LineStyle = {
  color: COLORS.WHITE_50,
  dash: [],
  width: 15,
  bezier: true,
};

export const LINES = {
  NONE: LINE_NONE,
  DEFAULT: LINE_DEFAULT,
  BLUE: LINE_BLUE,
  INSTRUCTION: LINE_INSTRUCTION,
  STEPS: LINE_STEPS,
  STEPS_COMPLETED: LINE_STEPS_COMPLETED,
  CURSOR_INPUT: LINE_CURSOR_INPUT,
  MODULE_PARTS: LINE_MODULE_PARTS,
  MODULE_PARTS_OPTIONAL: LINE_MODULE_PARTS_OPTIONAL,
  MODULE_PARTS_LOCKED: LINE_MODULE_PARTS_LOCKED,
  MODULE_PARTS_OPTIONAL_LOCKED: LINE_MODULE_PARTS_OPTIONAL_LOCKED,
  REFRAME_NEGATIVE: LINE_REFRAME_NEGATIVE,
  REFRAME_POSITIVE: LINE_REFRAME_POSITIVE,
  REFRAME_OBJ_NEGATIVE: LINE_REFRAME_OBJ_NEGATIVE,
  REFRAME_OBJ_POSITIVE: LINE_REFRAME_OBJ_POSITIVE,
  THIN: LINE_THIN,
  DOTTED: LINE_DOTTED,
  THICK: LINE_THICK,
} as const;

const BUTTON_CONTINUE: ButtonStyle = {
  icon: 'iconArrow',
  color: COLORS.BLUE_CRAYOLA,
  colorHighlight: COLORS.BLUE_HIGHLIGHT,
  size: 32,
  iconSize: 32,
  hoverShadow: true,
};

const BUTTON_CHECK: ButtonStyle = {
  icon: 'iconCheck',
  color: COLORS.BLUE_CRAYOLA,
  colorHighlight: COLORS.BLUE_HIGHLIGHT,
  size: 32,
  iconSize: 32,
  hoverShadow: true,
};

const BUTTON_CANVAS_REFOCUS: ButtonStyle = {
  icon: 'iconRefocus',
  color: COLORS.NEW_STYLE.BLUE_LIGHT,
  colorHighlight: COLORS.NEW_STYLE.BLUE_LIGHT,
  size: 32,
  iconSize: 54,
  hoverShadow: true,
};

const BUTTON_NEXT_INSTRUCTION: ButtonStyle = {
  icon: 'buttonContinue',
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  size: 32,
  iconSize: 24,
  hoverShadow: true,
};

const BUTTON_TO_RIGHT: ButtonStyle = {
  icon: 'iconToRight',
  color: COLORS.TRANSPARENT,
  colorHighlight: COLORS.WHITE_25,
  size: 32,
  iconSize: 32,
  hoverShadow: true,
};

const BUTTON_TO_LEFT: ButtonStyle = {
  icon: 'iconToRight',
  color: COLORS.TRANSPARENT,
  colorHighlight: COLORS.WHITE_25,
  size: 32,
  iconSize: 32,
  flipHorizontally: true,
  hoverShadow: true,
};

const BUTTON_EDIT: ButtonStyle = {
  icon: 'iconEdit',
  color: COLORS.BLACK_25,
  colorHighlight: COLORS.LIGHTGREY2_100,
  size: 32,
  iconSize: 32,
  hoverShadow: true,
};

const BUTTON_PLAY: ButtonStyle = {
  icon: 'iconPlay',
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  size: 22,
  iconSize: 22,
  hoverShadow: true,
};

const BUTTON_SUBS_SHOW: ButtonStyle = {
  icon: 'iconSubs',
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  size: 22,
  iconSize: 25,
  hoverShadow: true,
};

const BUTTON_SUBS_HIDE: ButtonStyle = {
  ...BUTTON_SUBS_SHOW,
  icon: 'iconSubsFill',
};

const BUTTON_PAUSE: ButtonStyle = {
  icon: 'iconPause',
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  size: 22,
  iconSize: 22,
  hoverShadow: true,
};

const BUTTON_CLOSE: ButtonStyle = {
  icon: 'iconQuit',
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  size: 22,
  iconSize: 22,
  hoverShadow: true,
};

const BUTTON_REMOVE: ButtonStyle = {
  icon: 'iconRemove',
  color: COLORS.TRANSPARENT,
  colorHighlight: COLORS.WHITE_25,
  size: 32,
  iconSize: 32,
  hoverShadow: true,
};

const BUTTON_DEVICE_ERROR: ButtonStyle = {
  icon: 'warning',
  color: COLORS.WARNING,
  colorHighlight: COLORS.WARNING,
  size: 10,
  iconSize: 11,
  hoverShadow: true,
};

/////////////

const BUTTON_CHAT_SEND: ButtonStyle = {
  icon: 'iconSendMsg',
  color: COLORS.WHITE_25,
  colorHighlight: COLORS.WHITE_50,
  size: 32,
  iconSize: 32,
  hoverShadow: true,
};

export const BUTTON_STYLES = {
  CONTINUE: BUTTON_CONTINUE,
  CHECK: BUTTON_CHECK,
  TO_LEFT: BUTTON_TO_LEFT,
  TO_RIGHT: BUTTON_TO_RIGHT,
  EDIT: BUTTON_EDIT,
  PLAY: BUTTON_PLAY,
  SUBS_SHOW: BUTTON_SUBS_SHOW,
  SUBS_HIDE: BUTTON_SUBS_HIDE,
  PAUSE: BUTTON_PAUSE,
  REMOVE: BUTTON_REMOVE,
  NEXT_INSTRUCTION: BUTTON_NEXT_INSTRUCTION,
  CLOSE: BUTTON_CLOSE,

  CHAT_SEND: BUTTON_CHAT_SEND,
  CANVAS_RECENTER: BUTTON_CANVAS_REFOCUS,
} as const;

const SHAPE_STYLE_NONE: RenderShapeStyle = {
  fillColor: [0, 0, 0, 0],
  strokeWeight: 0,
  strokeColor: [0, 0, 0, 0],
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_STYLE_TEXT_OUTLINE: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  strokeWeight: 1,
  strokeColor: COLORS.WHITE_50,
};

const SHAPE_STYLE_OUTLINE_THIN: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  strokeWeight: 0.7,
  strokeColor: COLORS.WHITE_100,
  gradientId: 'TEXT_BLOCK',
};

const SHAPE_STYLE_TEXT_HIGHLIGHT: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  strokeWeight: 3,
  fillColor: COLORS.WHITE_25,
  strokeColor: COLORS.WHITE_50,
};

const SHAPE_NAVIGATION_NODE_RING: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  strokeWeight: 2,
  strokeColor: COLORS.WHITE_100,
  lineDash: [5, 5],
};

const SHAPE_NAVIGATION_NODE_RING_NEW: RenderShapeStyle = {
  fillColor: COLORS.NEW_STYLE.GREEN_LIGHT,
  strokeWeight: 0,
  strokeColor: COLORS.TRANSPARENT,
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_NAVIGATION_NODE_RING_READY_NEW: RenderShapeStyle = {
  fillColor: COLORS.TRANSPARENT,
  strokeWeight: 2,
  strokeColor: COLORS.WHITE_100,
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_NAVIGATION_NODE_RING_FINISH: RenderShapeStyle = {
  fillColor: COLORS.NEW_STYLE.RED,
  strokeWeight: 0,
  strokeColor: COLORS.TRANSPARENT,
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_NAVIGATION_NODE_BASE: RenderShapeStyle = {
  fillColor: COLORS.WHITE_25,
  strokeWeight: 0,
  strokeColor: COLORS.TRANSPARENT,
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_NAVIGATION_NODE_RING_READY: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_25,
  strokeWeight: 0,
  strokeColor: COLORS.WHITE_100,
};

const SHAPE_LIVE_SESSION_RING: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_10,

  strokeColor: COLORS.WHITE_50,
};

const SHAPE_LIVE_SESSION_RING_OPEN: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_25,
  strokeWeight: 1,
  strokeColor: COLORS.WHITE_100,
};

const SHAPE_LIVE_SESSION_DATE_TIME: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.GUNMETAL,
};

const SHAPE_STYLE_DEBUG: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: [...COLORS.RED_DARK, 150],
  strokeWeight: 1,
  strokeColor: COLORS.RED_DARK,
};

const SHAPE_STYLE_BLACK: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.BLACK_100,
};

const SHAPE_STYLE_WHITE: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_100,
};

const SHAPE_STYLE_WHITE_50: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_50,
};

const SHAPE_STYLE_WHITE_75: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_75,
};

const SHAPE_STYLE_WHITE_25: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_25,
};

const SHAPE_STYLE_DROP_BACK: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.BLACK_10,
};

const SHAPE_STYLE_DROP_ACTIVE: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 0.6,
  strokeColor: COLORS.WHITE_100,
};

const SHAPE_STYLE_PROGRESS_BACKGROUND: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 0.6,
  strokeColor: COLORS.WHITE_100,
};

const SHAPE_STYLE_PROGRESS_BACKGROUND_HL: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 3,
  strokeColor: COLORS.WHITE_100,
};

const SHAPE_STYLE_PROGRESS_FILL: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.BLACK_25,
  strokeWeight: 2,
  strokeColor: COLORS.WHITE_50,
};

const SHAPE_STYLE_DROP_ACTIVE_HINT: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 2,
  strokeColor: COLORS.WHITE_100,
};

const SHAPE_STYLE_DROP_ACTIVE_HINT_2: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 4,
  strokeColor: COLORS.WHITE_100,
};

const SHAPE_ROOM_NODE_RING_1: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 1.4,
  strokeColor: [255, 255, 255, 255],
};

const SHAPE_SOE_CANVAS: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 1,
  strokeColor: COLORS.WHITE_50,
};

const SHAPE_SOCIAL_ROOM_NODE: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 0.8,
  strokeColor: COLORS.WHITE_100,
};

const SHAPE_SOCIAL_ROOM_NODE_HOVER: RenderShapeStyle = {
  ...SHAPE_SOCIAL_ROOM_NODE,
  strokeWeight: 4,
};

const SHAPE_ROOM_NODE_RING_2: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 1,
  strokeColor: [255, 255, 255, 255],
};

const SHAPE_TEXT_EDITOR: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: [0, 0, 0, 0],
  lineDash: [5, 5, 5],
  strokeWeight: 1,
  strokeColor: COLORS.WHITE_100,
};

const SHAPE_SLIDER_KNOB: RenderShapeStyle = {
  fillColor: COLORS.BLUE_CRAYOLA,
  strokeWeight: 0,
  strokeColor: COLORS.TRANSPARENT,
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_SLIDER_KNOB_HL: RenderShapeStyle = {
  fillColor: COLORS.BLUE_HIGHLIGHT,
  strokeWeight: 0,
  strokeColor: COLORS.TRANSPARENT,
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_VIDEOPLAYER_KNOB: RenderShapeStyle = {
  fillColor: COLORS.NEW_STYLE.BLUE_LIGHT,
  strokeWeight: 0,
  strokeColor: COLORS.WHITE_100,
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_VIDEOPLAYER_GRADIENT: RenderShapeStyle = {
  fillColor: COLORS.TRANSPARENT,
  strokeWeight: 0,
  strokeColor: COLORS.TRANSPARENT,
  lineDash: [],
  lineDashOffset: 0,
  gradientId: 'BLACK',
  shadow: null,
};

const SHAPE_VIDEOPLAYER_KNOB_HL: RenderShapeStyle = {
  fillColor: COLORS.NEW_STYLE.BLUE_LIGHT,
  strokeWeight: 12,
  strokeColor: COLORS.WHITE_100,
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_DEBUG_ANCHOR: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 5,
  strokeColor: [255, 0, 0, 255],
};

const SHAPE_DEBUG_GREEN: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: [0, 255, 0, 50],
  strokeWeight: 5,
  strokeColor: [0, 255, 0, 255],
};

const SHAPE_DEBUG_RED: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: [255, 0, 0, 50],
  strokeWeight: 5,
  strokeColor: [255, 0, 0, 255],
};

const SHAPE_DEBUG_MOUSE_OVER: RenderShapeStyle = {
  fillColor: [255, 0, 0, 50],
  strokeWeight: 2,
  strokeColor: [255, 0, 0, 255],
  lineDash: [],
  lineDashOffset: 0,
  shadow: null,
};

const SHAPE_DEBUG_HIGHLIGHT: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 5,
  strokeColor: [0, 255, 0, 255],
};

const SHAPE_CHAT_CONTAINER: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.BLACK_25,
};

const SHAPE_UI_GREY: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.BLACK_25,
};

const SHAPE_UI_GREEN: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.GREEN_25,
};

const SHAPE_UI_GREEN_NEW: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.NEW_STYLE.GREEN_LIGHT,
};

const SHAPE_UI_GREEN_DARK: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.NEW_STYLE.GREEN_DARK,
};

const SHAPE_UI_BLUE_NEW: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.NEW_STYLE.BLUE_LIGHT,
};

const SHAPE_UI_BLUE_DARK: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.NEW_STYLE.BLUE_DARK,
};

const SHAPE_UI_PURPLE_NEW: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.NEW_STYLE.PURPLE_LIGHT,
};

const SHAPE_UI_PURPLE_DARK: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.NEW_STYLE.PURPLE_DARK,
};

const SHAPE_UI_FINISHED: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.BLACK_25,
  strokeWeight: 5,
  strokeColor: COLORS.GREEN_100,
};

const SHAPE_UI_RED: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.RED_25,
};

const SHAPE_UI_ORANGE: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.ORANGE_25,
};

const SHAPE_LOBBY_USERLIST: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_10,
};

const SHAPE_BUTTON_HOLD_RED: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.RED_SALSA,
};

const SHAPE_INFOGRAPHIC_HOTSPOT: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,

  strokeWeight: 4,
  strokeColor: COLORS.WHITE_50,
};

const SHAPE_BREAKOUT_CONTROLLER_BACK: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.DARKGREY1_100,
};

const SHAPE_TEXT_INPUT_DEFAULT: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_75,
  strokeWeight: 0,
  strokeColor: COLORS.LIGHTGREY2_100,
};

const SHAPE_TEXT_INPUT_HOVER: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_75,
  strokeWeight: 5,
  strokeColor: COLORS.NEW_STYLE.BLUE_LIGHT,
};

const SHAPE_TEXT_INPUT_ACTIVE: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_100,
  strokeWeight: 5,
  strokeColor: COLORS.NEW_STYLE.BLUE_LIGHT,
};

const SHAPE_TEXT_INPUT_DISABLED: RenderShapeStyle = {
  ...SHAPE_STYLE_NONE,
  fillColor: COLORS.WHITE_75,
  strokeWeight: 0,
};

export const SHAPE_STYLES = {
  NONE: SHAPE_STYLE_NONE,

  DEBUG: SHAPE_STYLE_DEBUG,
  DEBUG_ANCHOR: SHAPE_DEBUG_ANCHOR,
  DEBUG_MOUSE_OVER: SHAPE_DEBUG_MOUSE_OVER,
  DEBUG_HIGHLIGHT: SHAPE_DEBUG_HIGHLIGHT,
  DEBUG_GREEN: SHAPE_DEBUG_GREEN,
  DEBUG_RED: SHAPE_DEBUG_RED,

  BLACK: SHAPE_STYLE_BLACK,
  WHITE: SHAPE_STYLE_WHITE,
  WHITE_25: SHAPE_STYLE_WHITE_25,
  WHITE_50: SHAPE_STYLE_WHITE_50,
  WHITE_75: SHAPE_STYLE_WHITE_75,
  UI_GREY: SHAPE_UI_GREY,
  UI_GREEN: SHAPE_UI_GREEN,
  UI_FINISHED: SHAPE_UI_FINISHED,
  UI_RED: SHAPE_UI_RED,
  UI_ORANGE: SHAPE_UI_ORANGE,

  GREEN_NEW: SHAPE_UI_GREEN_NEW,
  GREEN_DARK_NEW: SHAPE_UI_GREEN_DARK,
  BLUE_NEW: SHAPE_UI_BLUE_NEW,
  BLUE_DARK_NEW: SHAPE_UI_BLUE_DARK,
  PURPLE_NEW: SHAPE_UI_PURPLE_NEW,
  PURPLE_DARK_NEW: SHAPE_UI_PURPLE_DARK,

  DROP_BACK: SHAPE_STYLE_DROP_BACK,
  DROP_ACTIVE: SHAPE_STYLE_DROP_ACTIVE,
  DROP_ACTIVE_HINT: SHAPE_STYLE_DROP_ACTIVE_HINT,
  DROP_ACTIVE_HINT_2: SHAPE_STYLE_DROP_ACTIVE_HINT_2,
  ROOM_NODE_RING_1: SHAPE_ROOM_NODE_RING_1,
  ROOM_NODE_RING_2: SHAPE_ROOM_NODE_RING_2,

  TEXT_EDITOR: SHAPE_TEXT_EDITOR,

  TEXT_INPUT: {
    DEFAULT: SHAPE_TEXT_INPUT_DEFAULT,
    HOVER: SHAPE_TEXT_INPUT_HOVER,
    ACTIVE: SHAPE_TEXT_INPUT_ACTIVE,
    DISABLED: SHAPE_TEXT_INPUT_DISABLED,
  },

  SLIDER: {
    KNOB: SHAPE_SLIDER_KNOB,
    KNOB_HL: SHAPE_SLIDER_KNOB_HL,
  },

  VIDEOPLAYER: {
    KNOB: SHAPE_VIDEOPLAYER_KNOB,
    KNOB_HL: SHAPE_VIDEOPLAYER_KNOB_HL,
    GRADIENT: SHAPE_VIDEOPLAYER_GRADIENT,
  },

  BREAKOUT_CONTROLLER: {
    BACKGROUND: SHAPE_BREAKOUT_CONTROLLER_BACK,
  },

  CHAT_CONTAINER: SHAPE_CHAT_CONTAINER,
  INFOGRAPHIC_HOTSPOT: SHAPE_INFOGRAPHIC_HOTSPOT,

  PROGRESS_BACKGROUND: SHAPE_STYLE_PROGRESS_BACKGROUND,
  PROGRESS_BACKGROUND_HL: SHAPE_STYLE_PROGRESS_BACKGROUND_HL,
  PROGRESS_FILL: SHAPE_STYLE_PROGRESS_FILL,

  SOE_CANVAS: SHAPE_SOE_CANVAS,

  SOCIAL_ROOM_NODE: SHAPE_SOCIAL_ROOM_NODE,
  SOCIAL_ROOM_NODE_HOVER: SHAPE_SOCIAL_ROOM_NODE_HOVER,

  TEXT_OUTLINE: SHAPE_STYLE_TEXT_OUTLINE,

  OUTLINE_THIN: SHAPE_STYLE_OUTLINE_THIN,

  TEXT_HIGHLIGHT: SHAPE_STYLE_TEXT_HIGHLIGHT,

  NAVIGATION_NODE_RING: SHAPE_NAVIGATION_NODE_RING,
  NAVIGATION_NODE_NEW: {
    BASE: SHAPE_NAVIGATION_NODE_BASE,
    BUTTON: SHAPE_NAVIGATION_NODE_RING_NEW,
    BUTTON_ALL_READY: SHAPE_NAVIGATION_NODE_RING_READY_NEW,
    BUTTON_FINISH: SHAPE_NAVIGATION_NODE_RING_FINISH,
  },
  NAVIGATION_NODE_RING_READY: SHAPE_NAVIGATION_NODE_RING_READY,

  LIVE_SESSION_RING: SHAPE_LIVE_SESSION_RING,
  LIVE_SESSION_RING_OPEN: SHAPE_LIVE_SESSION_RING_OPEN,
  LIVE_SESSION_DATE_TIME: SHAPE_LIVE_SESSION_DATE_TIME,

  LOBBY_USERLIST: SHAPE_LOBBY_USERLIST,
  BUTTON_HOLD_RED: SHAPE_BUTTON_HOLD_RED,
} as const;

const GRADIENT_BLACK: GradientData = {
  x0: 0,
  y0: -30,
  x1: 0,
  y1: 30,
  colorStops: [
    { offset: 0, color: COLORS.TRANSPARENT },
    { offset: 1, color: COLORS.BLACK_50 },
    // { offset: 0.75, color: COLORS.TRANSPARENT },
    // { offset: 1, color: COLORS.BLACK_10 }
  ],
};

const GRADIENT_TEXT_BLOCK: GradientData = {
  x0: -100,
  y0: -100,
  x1: 100,
  y1: 100,
  colorStops: [
    { offset: 0, color: COLORS.WHITE_10 },
    { offset: 0.5, color: COLORS.TRANSPARENT },
    // { offset: 0.75, color: COLORS.TRANSPARENT },
    // { offset: 1, color: COLORS.BLACK_10 }
  ],
};

const GRADIENT_WHITE_PANEL: GradientData = {
  x0: -30,
  y0: -30,
  x1: 30,
  y1: 30,
  colorStops: [
    { offset: 0, color: COLORS.WHITE_25 },
    { offset: 1, color: COLORS.WHITE_50 },
    // { offset: 0.75, color: COLORS.TRANSPARENT },
    // { offset: 1, color: COLORS.BLACK_10 }
  ],
};

export const GRADIENTS: { [key in string]: GradientData } = {
  BLACK: GRADIENT_BLACK,
  TEXT_BLOCK: GRADIENT_TEXT_BLOCK,
  WHITE_PANEL: GRADIENT_WHITE_PANEL,
};

export const DEFAULT_TEXT_STYLE: TextStyle = {
  color: COLORS.WHITE_100,
  fontSize: 20,
  fontId: 'font_regular',
  lineHeight: 1.2,
} as const;

export const TEXT_STYLE_TEXT_INPUT: TextStyle = {
  color: COLORS.BLACK_100,
  fontSize: 20,
  fontId: 'font_regular',
  lineHeight: 1.2,
} as const;

export const TEXT_STYLE_TEXT_INPUT_INFO: TextStyle = {
  color: COLORS.WHITE_75,
  fontSize: 16,
  fontId: 'font_regular',
  lineHeight: 1,
} as const;
